import React, { useContext, useEffect, useState } from 'react'

import { CloseButtonPosition } from '@acorns/web-components'
import { PromptPortal, useRemoteStrings } from '@acorns/web-utils'
import { compose } from 'recompose'

import RoundUpsImage from 'assets/images/roundups-interstitial.svg'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import platform from 'components/Trackers/platform'
import { ActionType, PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import { MoreInfoPrompt } from './MoreInfoPrompt'
import {
  ActionWrapper,
  Container,
  ModalBody,
  ModalContent,
  ModalSeparator,
  ModalSubtitle,
  ModalTitle,
  StyledBody,
  StyledButton,
  StyledImage,
  StyledLayout,
  StyledModal,
  StyledTextButton,
  StyledTitle,
} from './styled-components'

type Props = {
  ctaLabel: string
  handleNextClick: () => void
  header: string
  text: string
  secondaryCtaLabel: string
  subheader: string
} & HeroTextProps &
  WithTieredRegistrationProps &
  WithFlowRouterProps

const RoundUpsInterstitialScreenViewedEvent = {
  feature: 'Registration: Round-Ups Interstitial',
  event_description: 'Screen Viewed',
  platform,
  object: 'Screen',
  action: 'Viewed',
  object_name: 'registrationRoundUpsInterstitial',
  style: 'main',
  screen: 'registrationRoundUpsInterstitial',
  screen_name: 'registrationRoundUpsInterstitial',
}

const NextButtonTappedEvent = {
  feature: 'Registration: Round-Ups Interstitial',
  event_description: 'Next button tapped',
  platform,
  object: 'Button',
  action: 'Tapped',
  object_name: 'registrationRoundUpsInterstitial',
  screen: 'registrationRoundUpsInterstitial',
  screen_name: 'registrationRoundUpsInterstitial',
}

const MoreInfoButtonTappedEvent = {
  feature: 'Registration: Round-Ups Interstitial',
  event_description: 'More Info button tapped',
  platform,
  object: 'Button',
  action: 'Tapped',
  object_name: 'registrationRoundUpsInterstitial',
  screen: 'registrationRoundUpsInterstitial',
  screen_name: 'registrationRoundUpsInterstitial',
}

const enhance = compose<Props, {}>(withHeroText, withFlowRouter, withTieredRegistration)

const RoundupsInterstitial: React.FunctionComponent<Props> = ({ heroText, route }) => {
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: RoundUpsInterstitialScreenViewedEvent,
      flow: getFlow(),
    })
  }, [])

  const handleMoreInfo = () => {
    trackEvent('Button Tapped', {
      eventData: MoreInfoButtonTappedEvent,
      flow: getFlow(),
    })
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleNext = () => {
    trackEvent('Button Tapped', {
      eventData: NextButtonTappedEvent,
      flow: getFlow(),
    })
    isDynamicFunnelEnabled
      ? routeToPage(PageName.RoundUpsInterstitial, ActionType.Continue)
      : route({ from: FlowRoute.RoundUpsInterstitial, action: 'continue' })
  }

  const handleSkip = () => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.RoundUpsInterstitial, ActionType.Skip)
      : route({ from: FlowRoute.RoundUpsInterstitial, action: 'skip' })
  }

  return (
    <>
      <Nav
        back={isDynamicFunnelEnabled && isBackEnabled(PageName.RoundUpsInterstitial)}
        linkText="Skip"
        onLink={handleSkip}
      />
      <StyledLayout>
        <Container>
          <StyledImage src={RoundUpsImage} data-testid={TestIDs.ROUNDUPS_INTERSTITIAL.IMAGE} />
          <StyledTitle>
            {getRemoteString({ key: 'invest.onboarding.round_ups_interstitial.header' })}
          </StyledTitle>
          <StyledBody>
            {getRemoteString({ key: 'invest.onboarding.round_ups_interstitial.body' })}
          </StyledBody>
        </Container>

        <ActionWrapper>
          <Separator />
          <StyledButton
            data-testid={TestIDs.ROUNDUPS_INTERSTITIAL.NEXT_BUTTON}
            onPress={handleNext}
          >
            {getRemoteString({ key: 'invest.onboarding.round_ups_interstitial.cta.next' })}
          </StyledButton>

          <StyledTextButton
            data-testid={TestIDs.ROUNDUPS_INTERSTITIAL.MORE_INFO}
            onPress={handleMoreInfo}
            styleType="flat"
            secondary
          >
            {heroText.secondaryCtaLabel}
          </StyledTextButton>
        </ActionWrapper>
      </StyledLayout>
      <PromptPortal shouldShow={isOpen}>
        <StyledModal
          isOpen={isOpen}
          onRequestClose={handleClose}
          closeButton={{ position: CloseButtonPosition.upperLeft, size: 16 }}
        >
          <ModalTitle>{MoreInfoPrompt.title}</ModalTitle>
          <ModalSeparator />
          <ModalSubtitle>{MoreInfoPrompt.subtitle}</ModalSubtitle>
          <ModalBody>
            {MoreInfoPrompt.message.map((paragraph, index) => (
              <ModalContent key={index}> {paragraph} </ModalContent>
            ))}
          </ModalBody>
        </StyledModal>
      </PromptPortal>
    </>
  )
}

export default enhance(RoundupsInterstitial)
